/**
 * Account list, import new, per-account menu: show key / delete.
 * "Import another account" requires PIN verification first.
 */

import { useState } from 'react'
import { formatAccountDisplay } from '@properwallet/wallet-core'
import { PIN_LENGTH } from './PinInput'
import PinInput from './PinInput'
import AccountAvatar from './AccountAvatar'
import { useWalletStore } from '../store/wallet.store'
import { useAppStore } from '../store/app.store'
import { useTranslation } from '../hooks/useTranslation'

export interface AccountSwitcherModalProps {
  open: boolean
  onClose: () => void
}

export default function AccountSwitcherModal({ open, onClose }: AccountSwitcherModalProps) {
  const { t } = useTranslation()
  const {
    accounts,
    activeAccount,
    setActiveAccount,
    navigate,
    removeAccount,
    revealPrivateKeyWithPin,
    revealPrivateKeyWithPassword,
    verifyPin,
    pinVerified,
  } = useWalletStore()
  const { setOnboardingMode } = useAppStore()

  const [menuFor, setMenuFor] = useState<string | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [revealFor, setRevealFor] = useState<string | null>(null)
  const [revealMode, setRevealMode] = useState<'pin' | 'password'>('pin')
  const [pinInput, setPinInput] = useState('')
  const [passInput, setPassInput] = useState('')
  const [revealedKey, setRevealedKey] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  /** PIN gate for "Import another account" */
  const [importPinGate, setImportPinGate] = useState(false)
  const [importPin, setImportPin] = useState('')
  const [importPinError, setImportPinError] = useState('')
  const [importPinBusy, setImportPinBusy] = useState(false)

  if (!open) return null

  const handleImportClick = () => {
    if (pinVerified) {
      // Session already unlocked — no need to re-verify
      setOnboardingMode('add-account')
      navigate('onboarding')
      onClose()
    } else {
      setImportPinGate(true)
      setImportPin('')
      setImportPinError('')
    }
  }

  const handleImportPinChange = async (v: string) => {
    setImportPin(v)
    setImportPinError('')
    if (v.length !== PIN_LENGTH) return
    setImportPinBusy(true)
    try {
      const ok = await verifyPin(v)
      if (!ok) {
        setImportPinError(t('pin.wrong'))
        setImportPin('')
        return
      }
      setImportPinGate(false)
      setImportPin('')
      setOnboardingMode('add-account')
      navigate('onboarding')
      onClose()
    } catch (e) {
      setImportPinError(e instanceof Error ? e.message : t('common.error'))
      setImportPin('')
    } finally {
      setImportPinBusy(false)
    }
  }

  const handleReveal = async () => {
    if (!revealFor) return
    setBusy(true)
    setError('')
    try {
      let key: string
      if (revealMode === 'pin') {
        key = await revealPrivateKeyWithPin(revealFor, pinInput)
      } else {
        key = await revealPrivateKeyWithPassword(revealFor, passInput)
      }
      setRevealedKey(key)
    } catch (e) {
      setError(e instanceof Error ? e.message : t('common.error'))
    } finally {
      setBusy(false)
    }
  }

  const handleDelete = async (name: string) => {
    setBusy(true)
    try {
      await removeAccount(name)
      if (useWalletStore.getState().accounts.length === 0) {
        setOnboardingMode('fresh')
      }
      setDeleteConfirm(null)
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : t('common.error'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <button type="button" className="absolute inset-0 bg-black/70" aria-label={t('common.close')} onClick={onClose} />
      <div className="relative w-full max-w-md max-h-[85vh] rounded-t-2xl sm:rounded-2xl bg-surface-1 border border-surface-3 shadow-xl flex flex-col overflow-hidden">
        <div className="flex justify-between items-center px-4 py-3 border-b border-surface-3">
          <h3 className="text-white font-semibold text-sm">{t('accounts.switchAccount')}</h3>
          <button type="button" onClick={onClose} className="text-gray-400 text-lg px-2" aria-label={t('common.close')}>
            ✕
          </button>
        </div>

        <div className="overflow-y-auto p-3 space-y-2">
          {accounts.map(acc => {
            const active = acc.name === activeAccount?.name
            return (
              <div
                key={acc.name}
                className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 ${
                  active ? 'border-brand bg-brand/5' : 'border-surface-3'
                }`}
              >
                <AccountAvatar avatarUrl={acc.avatarUrl} size="sm" />
                <button
                  type="button"
                  className="flex-1 text-left min-w-0"
                  onClick={() => {
                    void setActiveAccount(acc.name)
                    onClose()
                  }}
                >
                  <p className="text-white text-sm truncate">{formatAccountDisplay(acc)}</p>
                  <p className="text-[10px] text-gray-500 truncate">{acc.name}</p>
                </button>
                {active && <span className="text-brand text-sm">✓</span>}
                <div className="relative">
                  <button
                    type="button"
                    className="text-gray-400 px-2 py-1 text-lg"
                    onClick={() => setMenuFor(menuFor === acc.name ? null : acc.name)}
                    aria-label="Menu"
                  >
                    ⋮
                  </button>
                  {menuFor === acc.name && (
                    <div className="absolute right-0 top-full mt-1 z-10 bg-surface-2 border border-surface-3 rounded-lg py-1 min-w-[180px] shadow-lg">
                      <button
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm text-white hover:bg-surface-3"
                        onClick={() => {
                          setRevealFor(acc.name)
                          setRevealedKey(null)
                          setPinInput('')
                          setPassInput('')
                          setRevealMode('pin')
                          setMenuFor(null)
                        }}
                      >
                        {t('accounts.showPrivateKey')}
                      </button>
                      <button
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm text-danger hover:bg-surface-3"
                        onClick={() => {
                          setDeleteConfirm(acc.name)
                          setMenuFor(null)
                        }}
                      >
                        {t('accounts.deleteAccount')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )
          })}

          <button
            type="button"
            className="btn-primary w-full text-sm py-3 mt-2"
            onClick={() => { navigate('create-account'); onClose() }}
          >
            {t('tools.createAccountCard')}
          </button>

          <button type="button" className="btn-secondary w-full text-sm py-3 mt-2" onClick={handleImportClick}>
            {t('accounts.importNew')}
          </button>

          {error ? <p className="text-danger text-xs text-center">{error}</p> : null}
        </div>
      </div>

      {/* PIN gate for import */}
      {importPinGate && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80">
          <div className="bg-surface-1 rounded-2xl border border-surface-3 p-5 max-w-sm w-full space-y-4">
            <h3 className="text-white font-semibold text-center">{t('accounts.importNew')}</h3>
            <p className="text-sm text-gray-400 text-center">{t('pin.enter')}</p>
            <PinInput value={importPin} onChange={v => void handleImportPinChange(v)} disabled={importPinBusy} />
            {importPinError ? <p className="text-danger text-xs text-center">{importPinError}</p> : null}
            <button
              type="button"
              className="text-sm text-gray-400 w-full text-center"
              onClick={() => {
                setImportPinGate(false)
                setImportPin('')
                setImportPinError('')
              }}
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80">
          <div className="bg-surface-1 rounded-2xl border border-surface-3 p-4 max-w-sm w-full space-y-3">
            <p className="text-white text-sm">{t('accounts.deleteConfirm')}</p>
            <div className="flex gap-2">
              <button type="button" className="btn-secondary flex-1" onClick={() => setDeleteConfirm(null)}>
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="btn-primary flex-1 bg-danger border-danger"
                disabled={busy}
                onClick={() => void handleDelete(deleteConfirm)}
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {revealFor && !revealedKey && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80">
          <div className="bg-surface-1 rounded-2xl border border-surface-3 p-4 max-w-sm w-full space-y-3 max-h-[90vh] overflow-y-auto">
            <p className="text-warning text-xs">{t('accounts.privateKeyWarning')}</p>
            <div className="flex gap-2">
              <button
                type="button"
                className={`flex-1 py-2 rounded-lg text-sm ${revealMode === 'password' ? 'bg-brand/20 text-brand' : 'bg-surface-2 text-gray-400'}`}
                onClick={() => setRevealMode('password')}
              >
                {t('onboarding.setPassword')}
              </button>
              <button
                type="button"
                className={`flex-1 py-2 rounded-lg text-sm ${revealMode === 'pin' ? 'bg-brand/20 text-brand' : 'bg-surface-2 text-gray-400'}`}
                onClick={() => setRevealMode('pin')}
              >
                PIN
              </button>
            </div>
            {revealMode === 'pin' ? (
              <input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={PIN_LENGTH}
                value={pinInput}
                onChange={e => setPinInput(e.target.value.replace(/\D/g, '').slice(0, PIN_LENGTH))}
                className="input-field"
                placeholder="PIN"
              />
            ) : (
              <input
                type="password"
                value={passInput}
                onChange={e => setPassInput(e.target.value)}
                className="input-field"
                autoComplete="current-password"
              />
            )}
            {error ? <p className="text-danger text-xs">{error}</p> : null}
            <div className="flex gap-2">
              <button type="button" className="btn-secondary flex-1" onClick={() => setRevealFor(null)}>
                {t('common.cancel')}
              </button>
              <button type="button" className="btn-primary flex-1" disabled={busy} onClick={() => void handleReveal()}>
                {t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {revealedKey && revealFor && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80">
          <div className="bg-surface-1 rounded-2xl border border-surface-3 p-4 max-w-sm w-full space-y-3">
            <p className="text-xs text-gray-500">{t('accounts.showPrivateKey')}</p>
            <pre className="text-xs font-mono text-white break-all whitespace-pre-wrap bg-surface-2 p-3 rounded-lg max-h-40 overflow-y-auto">
              {revealedKey}
            </pre>
            <button
              type="button"
              className="btn-primary w-full"
              onClick={() => {
                setRevealedKey(null)
                setRevealFor(null)
                setPinInput('')
                setPassInput('')
              }}
            >
              {t('common.done')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
